import { Clock, Loader2, RotateCw, Sparkles, Users as UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { Source } from "@/hooks/use-sources";
import type { Stakeholder } from "@/hooks/use-stakeholders";
import { formatDate, formatRelativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ParticipantStack } from "./participant-stack";
import { SourceIcon, sourceTypeLabel } from "./source-icon";
import { categorizeSource, type SourceCategory } from "./source-status";

interface SourcesListProps {
  sources: Source[];
  stakeholders: Stakeholder[] | undefined;
  baselineConfirmed: boolean;
  isProcessing: (sourceId: string) => boolean;
  onSelectSource: (id: string) => void;
  onProcessClick: (sourceId: string) => void;
}

export function SourcesList({
  sources,
  stakeholders,
  baselineConfirmed,
  isProcessing,
  onSelectSource,
  onProcessClick,
}: SourcesListProps) {
  const failed: Source[] = [];
  const notProcessed: Source[] = [];
  const processed: Source[] = [];
  for (const s of sources) {
    const cat = categorizeSource(s);
    if (cat === "failed") failed.push(s);
    else if (cat === "processed") processed.push(s);
    else notProcessed.push(s); // notProcessed + processing both land here
  }

  const allCaughtUp = failed.length === 0 && notProcessed.length === 0;
  const processedGroups = groupByWeek(processed);

  return (
    <div className="space-y-8">
      {failed.length > 0 && (
        <FlatSection label="Failed" count={failed.length}>
          {failed.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              category="failed"
              stakeholders={stakeholders}
              baselineConfirmed={baselineConfirmed}
              isProcessing={isProcessing(s.id)}
              onSelectSource={onSelectSource}
              onProcessClick={onProcessClick}
            />
          ))}
        </FlatSection>
      )}

      {notProcessed.length > 0 && (
        <FlatSection label="Not yet processed" count={notProcessed.length}>
          {notProcessed.map((s) => {
            const cat = categorizeSource(s);
            return (
              <SourceRow
                key={s.id}
                source={s}
                category={cat}
                stakeholders={stakeholders}
                baselineConfirmed={baselineConfirmed}
                isProcessing={isProcessing(s.id)}
                onSelectSource={onSelectSource}
                onProcessClick={onProcessClick}
              />
            );
          })}
        </FlatSection>
      )}

      {allCaughtUp && processed.length > 0 && (
        <div className="text-sm text-muted-foreground italic">All caught up.</div>
      )}

      {processedGroups.map((group) => (
        <section key={group.label}>
          <header className="mb-3 flex items-baseline gap-3">
            <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              Processed · {group.label}
            </h2>
            <span className="text-xs text-muted-foreground">
              {group.items.length} {group.items.length === 1 ? "source" : "sources"}
            </span>
          </header>
          <div className="space-y-2">
            {group.items.map((s) => (
              <SourceRow
                key={s.id}
                source={s}
                category="processed"
                stakeholders={stakeholders}
                baselineConfirmed={baselineConfirmed}
                isProcessing={isProcessing(s.id)}
                onSelectSource={onSelectSource}
                onProcessClick={onProcessClick}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function FlatSection({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-3 flex items-baseline gap-3">
        <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
          {label}
        </h2>
        <span className="text-xs text-muted-foreground">({count})</span>
      </header>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

interface SourceRowProps {
  source: Source;
  category: SourceCategory;
  stakeholders: Stakeholder[] | undefined;
  baselineConfirmed: boolean;
  isProcessing: boolean;
  onSelectSource: (id: string) => void;
  onProcessClick: (sourceId: string) => void;
}

function SourceRow({
  source,
  category,
  stakeholders,
  baselineConfirmed,
  isProcessing,
  onSelectSource,
  onProcessClick,
}: SourceRowProps) {
  // While our own mutation is pending for this source, force "processing"
  // copy/visuals even before the next list refetch flips latestRun.status.
  const effectiveCategory: SourceCategory = isProcessing ? "processing" : category;
  const showAction = effectiveCategory === "failed" || effectiveCategory === "notProcessed";

  return (
    <div
      className={cn(
        "rounded-lg border border-[var(--color-border)] bg-card",
        "transition-colors hover:border-nymbl-blue hover:shadow-sm",
        "flex items-center gap-4 pr-4",
      )}
    >
      <button
        type="button"
        onClick={() => onSelectSource(source.id)}
        className="flex flex-1 items-center gap-4 text-left p-4 cursor-pointer min-w-0 focus-visible:outline-2 focus-visible:outline-nymbl-blue rounded-lg"
      >
        <SourceIcon type={source.type} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
              {sourceTypeLabel(source.type)}
            </span>
            <span className="text-[11px] text-muted-foreground">·</span>
            <span className="text-[11px] text-muted-foreground">
              {formatDate(source.sourceDate)}
            </span>
          </div>
          <div className="font-display text-sm font-semibold text-foreground truncate mt-0.5">
            {source.name}
          </div>
          <SubLine source={source} category={effectiveCategory} />
        </div>
        {effectiveCategory === "processed" && (
          <div className="flex items-center gap-4 shrink-0">
            {source.durationMin != null && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                {source.durationMin} min
              </span>
            )}
            {source.participantStakeholderIds.length > 0 ? (
              <ParticipantStack
                participantIds={source.participantStakeholderIds}
                stakeholders={stakeholders}
              />
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <UsersIcon className="h-3.5 w-3.5" />0
              </span>
            )}
          </div>
        )}
      </button>
      {showAction && (
        <ActionButton
          sourceId={source.id}
          isRetry={effectiveCategory === "failed"}
          disabled={!baselineConfirmed}
          onProcessClick={onProcessClick}
        />
      )}
    </div>
  );
}

function SubLine({ source, category }: { source: Source; category: SourceCategory }) {
  if (category === "processing") {
    return (
      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Processing…</span>
      </div>
    );
  }
  if (category === "failed") {
    return <div className="mt-0.5 text-xs text-warning">Failed — try again</div>;
  }
  if (category === "processed") {
    const when = source.processedAt ? formatRelativeTime(source.processedAt) : "recently";
    const count = source.itemsExtracted ?? 0;
    return (
      <div className="mt-0.5 text-xs text-muted-foreground">
        {count} {count === 1 ? "item" : "items"} extracted · {when}
      </div>
    );
  }
  // notProcessed
  return <div className="mt-0.5 text-xs text-muted-foreground">Not processed yet</div>;
}

function ActionButton({
  sourceId,
  isRetry,
  disabled,
  onProcessClick,
}: {
  sourceId: string;
  isRetry: boolean;
  disabled: boolean;
  onProcessClick: (sourceId: string) => void;
}) {
  const button = (
    <Button
      size="sm"
      variant={isRetry ? "outline" : "default"}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onProcessClick(sourceId);
      }}
      className={disabled ? "pointer-events-none" : undefined}
    >
      {isRetry ? (
        <>
          <RotateCw className="h-3.5 w-3.5" />
          Retry
        </>
      ) : (
        <>
          <Sparkles className="h-3.5 w-3.5" />
          Process AI
        </>
      )}
    </Button>
  );
  if (!disabled) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Disabled buttons swallow pointer events, so wrap for tooltip hover. */}
        <span className="inline-block">{button}</span>
      </TooltipTrigger>
      <TooltipContent side="left">
        Confirm a baseline in the Scope tab before processing sources.
      </TooltipContent>
    </Tooltip>
  );
}

function groupByWeek(sources: Source[]): { label: string; items: Source[] }[] {
  const buckets = new Map<string, { label: string; weekStart: number; items: Source[] }>();
  for (const s of sources) {
    const d = new Date(s.sourceDate);
    const weekStart = startOfWeek(d);
    const key = weekStart.toISOString();
    const label = weekLabel(weekStart);
    const bucket = buckets.get(key) ?? { label, weekStart: weekStart.getTime(), items: [] };
    bucket.items.push(s);
    buckets.set(key, bucket);
  }
  return Array.from(buckets.values()).sort((a, b) => b.weekStart - a.weekStart);
}

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const day = out.getDay();
  const diff = day === 0 ? -6 : 1 - day; // ISO Monday
  out.setDate(out.getDate() + diff);
  return out;
}

function weekLabel(weekStart: Date): string {
  const now = new Date();
  const thisWeek = startOfWeek(now);
  const diffDays = Math.round((thisWeek.getTime() - weekStart.getTime()) / 86_400_000);
  if (diffDays === 0) return "This week";
  if (diffDays === 7) return "Last week";
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const sameYear = weekStart.getFullYear() === end.getFullYear();
  const startFmt = weekStart.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
  const endFmt = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${startFmt} – ${endFmt}`;
}
